import { OLD_FRONTEND_URL } from '@/lib/constants/api';

const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1']);

/**
 * True only on a developer's own machine — never on any deployed
 * environment (staging or production). Checking the hostname in addition to
 * `import.meta.env.DEV` guards against dev-only tooling (router/query
 * devtools) leaking to end users if a deploy ever serves a dev-mode build.
 */
export const isLocalDev =
  import.meta.env.DEV && LOCAL_HOSTNAMES.has(window.location.hostname);

/** True on the staging deployment (`VITE_RETRACED_ENV=staging`). */
export const isStaging = import.meta.env.VITE_RETRACED_ENV === 'staging';

/**
 * True when a machine is driving the browser. Playwright, and every
 * WebDriver-based runner, sets `navigator.webdriver`.
 */
const isAutomatedBrowser =
  typeof navigator !== 'undefined' && navigator.webdriver === true;

/**
 * Dev tooling renders for a developer, not for a test runner.
 *
 * A local E2E run drives the app in dev mode (`pnpm dev:e2e`; CI serves a
 * production build), so `isLocalDev` alone leaves the router devtools mounted
 * for every such Playwright run, and they render the whole route tree into the DOM. Any short
 * `getByText` then becomes a strict-mode violation the moment that lazily
 * loaded panel arrives: `getByText('Product')` matched seventeen elements once
 * `product-mapping`, `productions` and `product-only/` were among the routes
 * (#5507). Because the panel loads lazily the collision is a race, so a spec
 * written against a quiet DOM passes until, one day, it does not.
 *
 * A test runner has no use for the panel, so it does not get one, and the
 * whole class of failure goes with it.
 */
export const showDevtools = isLocalDev && !isAutomatedBrowser;

/**
 * Hosts that get the "this is a test environment" bar, and what it calls them.
 *
 * An explicit list rather than a pattern, even though customer environments
 * are all shaped `dashboard.<company>.retraced.com` and could be matched. A
 * pattern would badge whatever happened to match next — staging, demo, any
 * host someone stands up later — and this bar telling a prospect mid-demo
 * that nothing they are seeing is real is worse than it being absent. Showing
 * it is a decision, so it is written down.
 *
 * Adding an environment is one line here. The label is spelled out rather
 * than derived from the hostname so it can read the way the company writes
 * its own name.
 */
const TEST_ENVIRONMENT_LABELS = new Map<string, string>([
  ['dashboard.zeeman-test.retraced.com', 'Zeeman'],
]);

/** The environment's display name, or '' on every host not listed above. */
export function environmentLabelFor(hostname: string): string {
  return TEST_ENVIRONMENT_LABELS.get(hostname) ?? '';
}

/** Empty everywhere except the hosts deliberately listed above. */
export const ENV_LABEL = environmentLabelFor(window.location.hostname);

/**
 * True only when a *separate* old dashboard exists to send the user back to.
 *
 * There are two ways for one not to exist, and both occur in the wild:
 *
 *  - Nothing configured. `OLD_FRONTEND_URL` still falls back to
 *    `localhost:3500` so developers can run v1 alongside, which means an unset
 *    variable on a real deployment would otherwise look like a configured one.
 *  - Configured, but pointing at this very app. Zeeman does exactly that, and
 *    the trip "back to the old dashboard" just reloads the page the user is
 *    already on.
 *
 * A v2-only deployment is a supported setup, not a misconfiguration, so
 * anything offering the trip back has to ask this rather than assume a URL.
 */
function resolveHasOldFrontend(): boolean {
  // The dev fallback only counts on a developer's own build.
  if (!import.meta.env.VITE_OLD_FRONTEND_URL && !import.meta.env.DEV)
    return false;
  try {
    return (
      new URL(OLD_FRONTEND_URL, window.location.href).origin !==
      window.location.origin
    );
  } catch {
    // An unparseable URL is no more usable than a missing one.
    return false;
  }
}

export const hasOldFrontend = resolveHasOldFrontend();
